import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import {
  EmployeesEmployeeSelectorInput,
  PerformanceEmployeesForSelectDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

import { Variables } from './targetEnums'

interface Props {
  variables: Variables
  onSelectEmployees: (targetEmployeeSelection: EmployeesEmployeeSelectorInput) => void
  autoSaveCustomSelection?: boolean
}

export const ManualEmployeeFilter = ({
  variables,
  onSelectEmployees,
  autoSaveCustomSelection,
}: Props) => {
  const [targetEmployeeSelection, setTargetEmployeeSelection] =
    useState<EmployeesEmployeeSelectorInput>({
      type: 'manual',
      ids: [],
    })

  const [fetched, setFetched] = useState(() => !variables.arguments?.length)

  const shouldSkipQuery = useMemo(() => {
    return (
      fetched ||
      !variables.arguments?.length ||
      (variables.targetStrategy !== 'by_employees' &&
        variables.targetStrategy !== 'manual_selection')
    )
  }, [fetched, variables.arguments, variables.targetStrategy])

  const { loading } = useQuery(PerformanceEmployeesForSelectDocument, {
    variables:
      variables.targetStrategy === 'by_employees'
        ? { ids: variables.arguments }
        : { accessIds: variables.arguments },
    skip: shouldSkipQuery,
    onCompleted: (data) => {
      const ids = data.employees.employeeNames.map((employee) => employee.id)
      const selection: EmployeesEmployeeSelectorInput = { type: 'manual', ids }
      setTargetEmployeeSelection(selection)
      setFetched(true)
    },
  })

  const hasChanges = useMemo(() => {
    if (targetEmployeeSelection.type !== 'manual') return true

    const currentIds = targetEmployeeSelection.ids ?? []
    const savedIds = variables.arguments ?? []

    if (currentIds.length !== savedIds.length) return true

    const savedSet = new Set(savedIds)
    return currentIds.some((id) => !savedSet.has(id))
  }, [targetEmployeeSelection, variables.arguments])

  if (loading) {
    return <Loading />
  }

  return (
    <Box gap="s12" width="full" data-testid="target-group-manual-employee-filter">
      <EmployeeSelectorV2
        label={i18n.t('common.employees')}
        messages={{
          placeholder: i18n.t('employees.select_employee.other'),
        }}
        value={targetEmployeeSelection.ids ?? undefined}
        onChange={(state) => {
          setTargetEmployeeSelection(state)
          if (autoSaveCustomSelection) {
            onSelectEmployees(state)
          }
        }}
        multiple
      />
      {!autoSaveCustomSelection && (
        <Box gap="s12" flexDirection="row" justifyContent="flexEnd" width="full">
          <F0Button
            label={i18n.t('common.save')}
            onClick={() => {
              onSelectEmployees(targetEmployeeSelection)
            }}
            disabled={!hasChanges}
          />
        </Box>
      )}
    </Box>
  )
}
